import { LogIn } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { getJobLinkToken, JOB_PATH } from '../lib/job-session';
import { isLinkSessionToken } from '../lib/link-session';
import { JobCard } from '../pages/job/JobShell';

/**
 * Keeps link sessions out of the portal.
 *
 * Someone who came in through a price-request link holds a real vetter
 * session, so the role check lets them through — but every portal operation
 * refuses them. Rather than a portal full of errors, send them back to their
 * job, or, when this tab doesn't know which job that is, explain the way in.
 */
export function PortalGate({ children }: { children: ReactNode }) {
	const { token, logout } = useAuth();

	if (!isLinkSessionToken(token)) return <>{children}</>;

	if (getJobLinkToken()) return <Navigate to={JOB_PATH} replace />;

	return (
		<JobCard>
			<h1 className="text-2xl font-medium -tracking-[0.02em] text-text-1">
				You&apos;re signed in with a price-request link
			</h1>
			<p className="mt-2 text-sm text-text-3">
				A link opens only the job it was sent for. Open it again from your email, or sign in
				with your password to see all your jobs.
			</p>
			<Button className="mt-5 w-full" onClick={logout}>
				<LogIn size={14} data-icon="inline-start" />
				Sign in with a password
			</Button>
		</JobCard>
	);
}
